import React, { Suspense, useCallback, useEffect } from 'react';
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { SITE_ROUTES, AUTH_ROUTES, ISiteRouteItem } from './router';
import '@assets/css/background.css';
import '@assets/css/scrollbar.css';
import { atomWithStorage } from 'jotai/utils';
import { Provider, useAtom } from 'jotai';
import DefaultLayout from '@layouts/DefaultLayout';
import ErrorPage from '@pages/error/ErrorPage';
import StudentService from '@services/GeniebookAPI/StudentServices.services';
import moment from 'moment';
import SkyBlueBackground from '@layouts/SkyBlueBackground';
import NavyBlueBackground from '@layouts/NavyBlueBackground';
import Cookies from 'js-cookie';
import { useQueryClient } from '@tanstack/react-query';
import { PopUp, Button } from 'geniebook-ui/dist';
import { AxiosInterceptor } from '@services/auth.service';
import { doLogout } from '@utils/logout';
import { ErrorBoundary } from 'react-error-boundary';
import GlobalStore from '@store/globalStore';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import growthbook from './config/GrowthBookSetting';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import PopupSliderMasteryLevel from '@components/tour/mastery_level/PopupSliderMasteryLevel';
import globalStore from '@store/globalStore';
import { showUnlockTrialPopUpAtom } from '@atom/popUpAtom';

const DashboardLayout = React.lazy(
  () => import('./app/layouts/DashboardLayout'),
);
const masteryCalculationTime = atomWithStorage<string | null>(
  'mastery_calculation_time',
  null,
);

function ErrorFallback({ error }: { error: string }) {
  console.error(`Error Rendering a Page - ${error}`);
  return (
    <>
      <ErrorPage
        code="500"
        showNavBar={false}
        customCopy={{
          header: (
            <div className={'text-left'}>
              Uh-oh! Something's not quite right
            </div>
          ),
          body: (
            <div className={'text-left'}>
              <span>To fix the issue, please try these:</span>
              <br />
              <ul className={'text-left list-disc ml-6'}>
                <li>Check your wifi. Refresh the page, and try again.</li>
                <li>
                  Use incognito mode. Click the three dots in your browser's top
                  right corner, then select 'New Incognito Window.' Or use the
                  shortcuts by pressing 'Ctrl + Shift + N' (or 'Cmd + Shift + N'
                  on a Mac).
                </li>
              </ul>
            </div>
          ),
        }}
      />
    </>
  );
}

const App = () => {
  polyfillCountryFlagEmojis();

  // to fix vh issue on iOS browsers
  // https://stackoverflow.com/questions/61308575/tailwind-h-screen-doesn-t-work-properly-on-mobile-devices
  const setCssVh = () => {
    document.documentElement.style.setProperty(
      '--vh',
      window.innerHeight * 0.01 + 'px',
    );
  };
  window.addEventListener('resize', setCssVh);
  setCssVh();

  // this function is to check if the mastery level already calculated or not. if not, will trigger the API call
  const [masteryTime, setMasteryTime] = useAtom(masteryCalculationTime);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('show_unlock_subject')) {
      globalStore.set(showUnlockTrialPopUpAtom, true);
    }
  }, [searchParams]);

  const authenticationPopupDefault = {
    buttons: <></>,
    show: false,
    msg: '',
    on_close: () => {},
    title: '',
  };
  const [authenticationPopup, setAuthenticationPopup] = React.useState(
    authenticationPopupDefault,
  );

  useEffect(() => {
    calculateMasteryLevel();
  }, [location.pathname]);

  //Checking User Authentication
  useEffect(() => {
    const uid = Cookies.get('uid');
    const is_login = Cookies.get('is_login');
    if (uid === undefined) {
      // Whitelist for non-authenticated routes from SITE_ROUTES except route.path === /login/password_reset/:token
      const isWhitelisted = SITE_ROUTES.some((route: ISiteRouteItem) => {
        if (route.path === '/login/password_reset/:token') {
          return (
            location.pathname.substring(0, 22) === `/login/password_reset/`
          );
        } else if (route.path === '/mockexamswebinars/:id') {
          return location.pathname.substring(0, 19) === `/mockexamswebinars/`;
        } else return route.path === location.pathname;
      });

      if (!isWhitelisted) {
        if (is_login === undefined) {
          setAuthenticationPopup(authenticationPopupDefault);
          navigate('/');
        } else {
          setAuthenticationPopup({
            ...authenticationPopup,
            buttons: (
              <>
                <Button className="min-w-full w-full" onClick={Logout}>
                  Login
                </Button>
              </>
            ),
            show: true,
            msg: `Don't worry though, we've saved all the latest changes you made to your work. Simply login to Geniebook to continue learning!`,
            on_close: Logout,
            title: 'Session has expired',
          });
        }
      }
    } else {
      if (is_login === undefined) Cookies.set('is_login', '1');
    }
  }, [location.pathname]);

  useEffect(() => {
    // Load features asynchronously when the app renders
    growthbook.loadFeatures();
  }, []);

  const Logout = () => {
    setAuthenticationPopup(authenticationPopupDefault);
    doLogout(navigate, queryClient);
  };

  async function calculateMasteryLevel() {
    try {
      const startOfDay = moment.utc().startOf('day');
      if (masteryTime) {
        const timeLogged = moment.utc(masteryTime);
        if (!timeLogged.isAfter(startOfDay.clone().add(8, 'hours'))) {
          await sendCalculateRequest();
        }
      } else {
        await sendCalculateRequest();
      }
    } catch (e) {
      /* empty */
    }
  }

  async function sendCalculateRequest() {
    const calculate = await StudentService.calculateMasteryLevel();
    if (!calculate.data?.error) {
      setMasteryTime(moment.utc().format());
    }
  }

  const mapRoute = useCallback((routes: ISiteRouteItem[]) => {
    return routes.map((route: ISiteRouteItem, index: number) => (
      <Route
        key={`route-auth-${route.path}-${index}`}
        path={route.path}
        element={
          route.without_layout ? (
            <Suspense fallback={<div>loading...</div>}>
              <NavyBlueBackground>
                <div key={`route-auth-layout-${index}`}>
                  <>
                    {authenticationPopup.show ? (
                      <>
                        <PopUp
                          onClose={authenticationPopup.on_close}
                          buttonGroup={authenticationPopup.buttons}
                          description={authenticationPopup.msg}
                          title={authenticationPopup.title}
                          disableBodyScroll={true}
                        />
                        <ErrorPage code="500" showNavBar={true} />
                      </>
                    ) : (
                      <AxiosInterceptor>{route.element}</AxiosInterceptor>
                    )}
                  </>
                </div>
              </NavyBlueBackground>
            </Suspense>
          ) : (
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={undefined}
            >
              <AxiosInterceptor>
                <Suspense fallback={<div>loading...</div>}>
                  <NavyBlueBackground>
                    <DashboardLayout key={`route-auth-layout-${index}`}>
                      <PopupSliderMasteryLevel />
                      {authenticationPopup.show ? (
                        <>
                          <PopUp
                            onClose={authenticationPopup.on_close}
                            buttonGroup={authenticationPopup.buttons}
                            description={authenticationPopup.msg}
                            title={authenticationPopup.title}
                            disableBodyScroll={true}
                          />
                          <ErrorPage code="500" showNavBar={false} />
                        </>
                      ) : (
                        <>{route.element}</>
                      )}
                    </DashboardLayout>
                  </NavyBlueBackground>
                </Suspense>
              </AxiosInterceptor>
            </ErrorBoundary>
          )
        }
      >
        {route.children?.length ? mapRoute(route.children) : undefined}
      </Route>
    ));
  }, []);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <Provider store={GlobalStore}>
        <Routes>
          {SITE_ROUTES.map((route: ISiteRouteItem, index: number) => (
            <Route
              key={`route-${index}`}
              path={route.path}
              element={<SkyBlueBackground>{route.element}</SkyBlueBackground>}
            />
          ))}

          {mapRoute(AUTH_ROUTES)}

          <Route
            path="*"
            element={
              <DefaultLayout>
                <ErrorPage code="404" />
              </DefaultLayout>
            }
          />
        </Routes>
      </Provider>
    </GrowthBookProvider>
  );
};

export default App;
